/**
 * Shopping list. Select days, scale for however many people you are cooking for,
 * and tick items into the trolley.
 *
 * Ticks are ordinary writes, so they obey the same rule as every other tick in this
 * app: a line shows as ticked only after the write has returned.
 */
import { useMemo, useState } from 'react';
import type { StoreApi } from './useStore.ts';
import { Banner, TickBar } from './components.tsx';
import type { TickState } from './components.tsx';
import { META } from '../plan/data.ts';
import { positionFor, dateForDayIndex, MAX_DAY_INDEX } from '../plan/dates.ts';
import {
  buildShoppingList, storedDays, storedPeople, clampPeople, formatQty,
  MIN_PEOPLE, MAX_PEOPLE,
} from '../plan/shopping.ts';
import type { ShoppingLine } from '../plan/shopping.ts';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HORIZON = 21;   // how many days forward the picker offers

export function Shopping({ api }: { api: StoreApi }) {
  const { state, durable } = api;
  const startDate = state.get<string>('setting:startDate') ?? META.defaultStartDate;
  const today = positionFor(startDate, new Date());
  const anchor = today.dayIndex;

  const defaultDays = useMemo(
    () => range(anchor, Math.min(anchor + 6, MAX_DAY_INDEX)), [anchor]);
  const days = storedDays(state, defaultDays);
  const people = storedPeople(state);
  const [showHidden, setShowHidden] = useState(false);

  const list = useMemo(
    () => buildShoppingList(state, days, people), [state, days, people]);

  const setDays = (next: number[]) =>
    void api.put('setting:shopDays', [...new Set(next)].sort((a, b) => a - b));
  const toggleDay = (d: number) =>
    setDays(days.includes(d) ? days.filter((x) => x !== d) : [...days, d]);
  const setPeople = (n: number) => void api.put('setting:shopPeople', clampPeople(n));

  const tickState = (slug: string, on: boolean): TickState => {
    const key = `shopTick:${slug}`;
    return api.pending.has(key) ? 'writing' : api.errorFor(key) ? 'failed' : on ? 'on' : 'off';
  };
  const isTicked = (l: ShoppingLine) => state.get(`shopTick:${l.slug}`) === 1;

  const remaining = list.quantified.filter((l) => !isTicked(l)).length
    + list.unquantified.filter((l) => !isTicked(l)).length;
  const totalLines = list.quantified.length + list.unquantified.length;

  const pickerDays = range(anchor, Math.min(anchor + HORIZON - 1, MAX_DAY_INDEX));

  return (
    <>
      <h1>Shopping</h1>
      <p className="sub">
        Every ingredient from every meal on the days you pick, added up. Meal swaps you
        have already made are included.
      </p>

      <h2>Days</h2>
      <div className="card">
        <div className="btns" style={{ marginTop: 0 }}>
          <button className="small" onClick={() => setDays(range(anchor, Math.min(anchor + 6, MAX_DAY_INDEX)))}>
            Next 7 days
          </button>
          <button className="small" onClick={() => {
            const weekStart = Math.floor(anchor / 7) * 7;
            setDays(range(weekStart, Math.min(weekStart + 6, MAX_DAY_INDEX)));
          }}>This week</button>
          <button className="small" onClick={() => {
            const weekEnd = Math.floor(anchor / 7) * 7 + 6;
            setDays(range(anchor, Math.min(weekEnd, MAX_DAY_INDEX)));
          }}>Rest of week</button>
          <button className="small" onClick={() => setDays([])}>Clear</button>
        </div>
        <div className="daypick">
          {pickerDays.map((d) => {
            const on = days.includes(d);
            return (
              <button key={d} className={`daychip${on ? ' on' : ''}`}
                      aria-pressed={on} disabled={!durable}
                      onClick={() => toggleDay(d)}>
                <span className="daychip-dow">{DOW[d % 7]}</span>
                <span className="daychip-date">{dateForDayIndex(startDate, d).slice(5)}</span>
                {d === anchor && <span className="daychip-today">today</span>}
              </button>
            );
          })}
        </div>
        <div className="sub" style={{ marginTop: 8 }}>
          {days.length === 0
            ? 'No days selected.'
            : `${days.length} day${days.length === 1 ? '' : 's'} · ${list.mealCount} meals`}
        </div>
        {api.errorFor('setting:shopDays') && (
          <Banner kind="bad" title="Not saved">{api.errorFor('setting:shopDays')}</Banner>
        )}
      </div>

      <h2>People</h2>
      <div className="card">
        <div className="stepper">
          <button className="navbtn" aria-label="Fewer people" disabled={!durable || people <= MIN_PEOPLE}
                  onClick={() => setPeople(people - 1)}>−</button>
          <div className="stepper-val" aria-live="polite">
            <strong>{people}</strong>
            <span className="dim"> {people === 1 ? 'person' : 'people'}</span>
          </div>
          <button className="navbtn" aria-label="More people" disabled={!durable || people >= MAX_PEOPLE}
                  onClick={() => setPeople(people + 1)}>+</button>
        </div>
        <p className="sub" style={{ marginBottom: 0 }}>
          Scales the shopping quantities only. Your own portions, calories and protein
          figures are unchanged everywhere else in the app.
        </p>
        {api.errorFor('setting:shopPeople') && (
          <Banner kind="bad" title="Not saved">{api.errorFor('setting:shopPeople')}</Banner>
        )}
      </div>

      {list.cookedLines.length > 0 && (
        <Banner kind="warn" title="Some weights are cooked, not purchase, weights">
          {list.cookedLines.map((l) => l.name).join(', ')}. Buy these dry and convert at the
          shelf — the plan does not carry a conversion factor and guessing one would move
          its carbohydrate figures. Anything listed as “dry” is already a purchase weight.
        </Banner>
      )}

      <h2>
        List <span className="dim" style={{ fontWeight: 400 }}>
          · {remaining} of {totalLines} left
        </span>
      </h2>

      {totalLines === 0 ? (
        <div className="card"><p className="sub" style={{ margin: 0 }}>
          Nothing to buy — select some days above.
        </p></div>
      ) : (
        <>
          <div className="card">
            {list.quantified.map((l) => (
              <Line key={`${l.slug}|${l.unit}`} api={api} line={l}
                    on={isTicked(l)} tickState={tickState} qty={formatQty(l)} />
            ))}
          </div>

          {list.unquantified.length > 0 && (
            <>
              <h2>No quantity given</h2>
              <div className="card">
                <p className="sub" style={{ marginTop: 0 }}>
                  These appear in the recipes without an amount. Some are real ingredients,
                  some are cooking notes. Hide anything you do not need to buy — it stays
                  hidden, and nothing is thrown away.
                </p>
                {list.unquantified.map((l) => (
                  <Line key={l.slug} api={api} line={l} on={isTicked(l)}
                        tickState={tickState} qty="" hideable />
                ))}
              </div>
            </>
          )}

          <div className="btns">
            <button disabled={!durable} onClick={async () => {
              if (!confirm('Untick everything on the list? The days and people stay as they are.')) return;
              for (const l of [...list.quantified, ...list.unquantified]) {
                if (isTicked(l)) await api.del(`shopTick:${l.slug}`);
              }
            }}>Clear ticks</button>
            {list.hidden.length > 0 && (
              <button onClick={() => setShowHidden((v) => !v)}>
                {showHidden ? 'Hide' : `Show hidden (${list.hidden.length})`}
              </button>
            )}
          </div>

          {showHidden && list.hidden.length > 0 && (
            <div className="card">
              {list.hidden.map((l) => (
                <div className="row" key={l.slug}>
                  <span className="k">{l.name}</span>
                  <span className="v">
                    <button className="linkbtn" disabled={!durable}
                            onClick={() => void api.del(`shopHide:${l.slug}`)}>restore</button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

function Line({ api, line, on, tickState, qty, hideable }: {
  api: StoreApi; line: ShoppingLine; on: boolean;
  tickState: (slug: string, on: boolean) => TickState; qty: string; hideable?: boolean;
}) {
  const key = `shopTick:${line.slug}`;
  return (
    <div className={`shopline${on ? ' done' : ''}`}>
      <TickBar
        state={tickState(line.slug, on)} disabled={!api.durable}
        onToggle={() => void api.setFlag(key, !on)}
        title={<>{qty && <strong className="shopqty">{qty}</strong>} {line.name}</>}
        sub={line.meals.length <= 2 ? line.meals.join(', ')
          : `${line.meals.slice(0, 2).join(', ')} +${line.meals.length - 2} more`}
      />
      {hideable && (
        <button className="hidebtn" aria-label={`Hide ${line.name}`} disabled={!api.durable}
                onClick={() => void api.put(`shopHide:${line.slug}`, 1)}>✕</button>
      )}
      {api.errorFor(key) && <div className="errline">Not saved — {api.errorFor(key)}</div>}
    </div>
  );
}

function range(from: number, to: number): number[] {
  const out: number[] = [];
  for (let i = from; i <= to; i++) out.push(i);
  return out;
}
